import { defineStore } from 'pinia'
import type Project from '@/types/project'
import type Status from '@/types/status'
import type Field from '@/types/field'
import type Contact from '@/types/contact'
import type Image from '@/types/image'
import type InteriorOption from '@/types/interiorOption'
import type { BuildingChooserDataV5 } from '@qispace/vue3-player'
import type Unit from '@/types/unit'
import { ProjectImagesCategory } from '@/types/image'

export const useProjectStore = defineStore('projectStore', () => {
    const project = ref<Project | null>(null)
    const projectStatuses = ref<Status[]>([])
    const projectFields = ref<Field[]>([])
    const projectImages = ref<Image[]>(null)
    const projectContacts = ref<Contact[]>(null)
    const projectInteriorOptions = ref<InteriorOption[]>(null)
    const buildingChooserData = ref<BuildingChooserDataV5>(null)

    const fetchProject = async (projectSlug: string): Promise<void> => {
        const { PUBLIC_API_URL, fetchData } = useAPI()
        project.value = await fetchData(`${PUBLIC_API_URL}/${projectSlug}/slug/`)
        buildingChooserData.value = project.value.buildingChooserData
    }

    const fetchProjectStatuses = async (projectSlug: string): Promise<void> => {
        const { PUBLIC_API_URL, fetchData } = useAPI()
        projectStatuses.value = await fetchData(`${PUBLIC_API_URL}/${projectSlug}/slug/status`)
    }

    const fetchProjectFields = async (projectSlug: string): Promise<void> => {
        const { PUBLIC_API_URL, fetchData } = useAPI()
        projectFields.value = await fetchData(`${PUBLIC_API_URL}/${projectSlug}/slug/field`)
    }

    const fetchProjectContacts = async (projectSlug: string): Promise<void> => {
        const { PUBLIC_API_URL, fetchData } = useAPI()
        projectContacts.value = await fetchData(`${PUBLIC_API_URL}/${projectSlug}/slug/contact`)
    }

    const fetchProjectImages = async (projectSlug: string): Promise<void> => {
        const { PUBLIC_API_URL, fetchData } = useAPI()
        projectImages.value = await fetchData(`${PUBLIC_API_URL}/${projectSlug}/slug/image`)
    }

    const fetchProjectInteriorOptions = async (projectSlug: string): Promise<void> => {
        const { PUBLIC_API_URL, fetchData } = useAPI()
        projectInteriorOptions.value = await fetchData(
            `${PUBLIC_API_URL}/${projectSlug}/slug/interiorOption`
        )
    }

    const getStatusByStatusId = (statusId: number) => {
        return projectStatuses.value.find((s) => s.id === statusId)
    }

    const projectWithFieldsAndStatuses = computed(() => {
        return { ...project.value, fields: projectFields.value, statuses: projectStatuses.value }
    })

    const sortableFields: Ref<Field[]> = computed(() => {
        return projectFields.value.filter((field) => field.sortable !== 'disabled')
    })

    const filterableFields = computed(() => {
        return projectFields.value
            .sort((a, b) => a.orderInList - b.orderInList)
            .filter((f) => f.filterable && (f.id !== null || f.key === 'building'))
    })

    const listViewFields = computed(() => {
        return projectFields.value.filter((field) => !field.hiddenInList && field.id)
    })

    const detailedViewFields = computed(() => {
        return projectFields.value
            .filter((field) => field.hiddenInDetails !== true && field.id)
            .sort((a, b) => a.orderInDetails - b.orderInDetails)
    })

    // TODO: project placeholder image
    const projectPlaceholderImage = () => {
        const { cdn } = useCDN()
        return `${cdn}/placeholders/project-placeholder.png`
    }

    const projectDefaultInterior = computed(() => {
        return projectInteriorOptions.value.find((option) => option.isDefault === true)
    })

    const getUnitStatus = computed(() => {
        return (unit: Partial<Unit>) => {
            const status = projectStatuses.value.find((status) => status.id === unit.status)
            return status ? status : undefined
        }
    })

    const projectImagesUnitFeaturedImages: Ref<Image[]> = computed(() => {
        if (projectImages.value === null) return []

        return projectImages.value.filter(
            (img) => img.category === ProjectImagesCategory.UnitFeaturedImage
        )
    })

    const getProjectImagesByCategory = (category: ProjectImagesCategory) => {
        if (projectImages.value === null) return []
        return projectImages.value.filter((img) => img.category === category)
    }

    const getProjectImagesInArray = (unitImages: Array<number>) => {
        if (projectImages.value === null) return []
        if (unitImages.length === 0) return []

        return unitImages
            .map((id) => {
                return projectImages.value.find((img) => img.id === id)
            })
            .filter((img) => img !== undefined)
    }

    const getProjectImageById = (projectImageId: number) => {
        if (projectImages.value === null) return undefined
        return projectImages.value.find((img) => img.id === projectImageId)
    }

    const projectPlayerData = computed(() => {
        return project.value.playerData.value
    })

    const projectLogo = computed(() => {
        // if (project.value.logo) {
        //     return `${useCDN().cdn}/content/${project.value.slug}/images/${project.value.logo}`
        // }

        return undefined
    })

    return {
        project,
        projectStatuses,
        projectFields,
        projectContacts,
        projectPlayerData,
        projectInteriorOptions,
        projectDefaultInterior,
        projectImages,
        projectImagesUnitFeaturedImages,
        getProjectImagesByCategory,
        projectWithFieldsAndStatuses,
        buildingChooserData,
        fetchProject,
        fetchProjectStatuses,
        getStatusByStatusId,
        fetchProjectFields,
        fetchProjectContacts,
        fetchProjectImages,
        fetchProjectInteriorOptions,
        getUnitStatus,
        sortableFields,
        filterableFields,
        listViewFields,
        detailedViewFields,
        getProjectImagesInArray,
        getProjectImageById,
        projectLogo
    }
})
